/* eslint-disable @typescript-eslint/no-restricted-imports */
import type { IsEqualCustomizer } from "lodash";
import { isDate, isEqual, isEqualWith, isObject, transform } from "lodash";

/**
 * https://gist.github.com/Yimiprod/7ee176597fef230d1451
 * This code is licensed under the terms of the MIT license
 *
 * Deep diff between two object, using lodash
 * @param  {Object} object Object compared
 * @param  {Object} base   Object to compare with
 * @param  {IsEqualCustomizer} customizer Equal Customizer
 * @param  {ignoreEmptyObjects} ignoreEmptyObjects defines whether to ignore empty properties or not
 * @return {Object}        Return a new object who represent the diff
 */
export function objectDifference(
  object: any,
  base: any,
  customizer?: IsEqualCustomizer,
  ignoreEmptyObjects = false
): any {
  function changes(object: any, base: any): any {
    return transform(object, function (result, value, key) {
      const areValuesEqual = customizer ? isEqualWith(value, base[key], customizer) : isEqual(value, base[key]);
      if (!areValuesEqual) {
        result[key] = isObject(value) && isObject(base[key]) ? changes(value, base[key]) : value;
        if (isEmptyObject(result[key]) && ignoreEmptyObjects) delete result[key];
      }
    });
  }
  if (object && base) return changes(object, base);
  else return object ?? base;
}

export const IsEqualEntityCustomizer = (a: any, b: any): boolean => {
  const c1 = isEqual(a, b);
  const c2 = isEmptyObject(a) && isEmptyObject(b);
  const c3 = a === false && b == null;
  return c1 || c2 || c3;
};

//https://medium.com/@trmaphi/lodash-isempty-value-you-might-be-using-it-the-wrong-way-d83210d7decf
export function isEmptyString(value: any): boolean {
  return typeof value === "string" && value.trim().length === 0;
}

//lodash isEmpty(1) === true
export function isEmptyObject(value: any): boolean {
  function checkIsEmptyString(): boolean {
    return isEmptyString(value);
  }
  function checkIsEmptyObject(): boolean {
    if (isDate(value)) return false;
    return (
      typeof value === "object" &&
      (Object.keys(value).length === 0 || Object.values(value).every((x) => isEmptyObject(x)))
    );
  }

  return value === undefined || value === null || checkIsEmptyObject() || checkIsEmptyString();
}
